import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Users, Euro, Loader2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import SubscriptionEditModal from '../../components/Admin/SubscriptionEditModal';

const PendingValidations = () => {
  const [validations, setValidations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchValidations();
  }, []);

  const fetchValidations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/pending-validations');
      setValidations(response.data);
    } catch (error) {
      console.error('Error fetching validations:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCredentialsModal = (validation) => {
    setSelectedValidation(validation);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  // Build client & subscription objects for SubscriptionEditModal
  const modalClient = selectedValidation ? {
    id: selectedValidation.client_id,
    full_name: selectedValidation.client_name,
    email: selectedValidation.client_email
  } : null;

  const modalSubscription = selectedValidation ? {
    id: selectedValidation.subscription_id,
    status: 'pending_credentials',
    status_icon: 'yellow',
    start_date: selectedValidation.created_at,
    end_date: selectedValidation.end_date || null,
    days_remaining: selectedValidation.days_remaining || null
  } : null;

  return (
    <div data-testid="pending-validations">
      {/* Counter */}
      <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-4 rounded-lg mb-6">
        <div className="flex items-center gap-4">
          <AlertTriangle className="h-6 w-6 text-[#FFB800]" />
          <div>
            <h3 className="text-lg font-bold text-white">{validations.length} validation{validations.length > 1 ? 's' : ''} en attente</h3>
            <p className="text-[#B8B8BA] text-xs">
              Total des gains bloqués : {validations.reduce((sum, v) => sum + v.total_pending_amount, 0).toFixed(2)}€
            </p>
          </div>
        </div>
      </div>

      {/* List */}
      {validations.length === 0 ? (
        <div className="bg-[#1A1A1D] p-12 rounded-lg border border-[#3A3A3D]/40 text-center">
          <CheckCircle className="h-16 w-16 text-[#00FF88] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Tout est à jour !</h3>
          <p className="text-[#B8B8BA] text-sm">Aucune validation en attente</p>
        </div>
      ) : (
        <div className="space-y-4">
          {validations.map((validation) => (
            <motion.div
              key={validation.subscription_id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40"
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Client info */}
                <div>
                  <h3 className="text-white font-bold mb-2 flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-[#00FFFF]" />Informations client
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-[#6B6B6E]">Nom : </span><span className="text-white">{validation.client_name}</span></div>
                    <div><span className="text-[#6B6B6E]">Email : </span><span className="text-white">{validation.client_email}</span></div>
                    <div><span className="text-[#6B6B6E]">Créé : </span><span className="text-white">{formatDate(validation.created_at)}</span></div>
                    {validation.vendor_info && (
                      <div className="mt-2 pt-2 border-t border-[#3A3A3D]">
                        <span className="text-[#6B6B6E]">Vendeur : </span><span className="text-[#00FFFF]">{validation.vendor_info.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pending gains */}
                <div>
                  <h3 className="text-white font-bold mb-2 flex items-center gap-2 text-sm">
                    <Euro className="h-4 w-4 text-[#FFB800]" />Gains bloqués ({validation.total_pending_amount.toFixed(2)}€)
                  </h3>
                  <div className="space-y-2">
                    {validation.pending_gains.map((gain, idx) => (
                      <div key={idx} className="bg-[#222226] p-2.5 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-white text-xs font-semibold">
                              {gain.type === 'commission' ? 'Commission vendeur' : 'Crédit parrain'}
                            </div>
                            <div className="text-[#6B6B6E] text-xs">{gain.beneficiary_email}</div>
                          </div>
                          <div className="text-[#FFB800] font-bold text-sm">{gain.amount}€</div>
                        </div>
                      </div>
                    ))}
                    {validation.pending_gains.length === 0 && (
                      <p className="text-[#6B6B6E] text-xs">Aucun gain associé</p>
                    )}
                  </div>
                </div>

                {/* Action */}
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => openCredentialsModal(validation)}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid={`assign-iptv-${validation.subscription_id}`}
                  >
                    <CheckCircle className="h-5 w-5" />
                    Attribuer accès IPTV
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* SubscriptionEditModal - same as client management */}
      {showModal && modalClient && modalSubscription && (
        <SubscriptionEditModal
          client={modalClient}
          subscription={modalSubscription}
          onClose={() => { setShowModal(false); setSelectedValidation(null); }}
          onSuccess={() => { fetchValidations(); setShowModal(false); setSelectedValidation(null); }}
        />
      )}
    </div>
  );
};

export default PendingValidations;
